import { type FC, useState, useCallback, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer.tsx';
import { trackPageView } from '../shared/helpers/tracking.ts';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers.jsx';
import { getBuyerId } from 'dibs-cookie-jar';
import { ProjectBreadcrumbs } from './ProjectBreadcrumbs.tsx';
import InteriorProjectContent from './InteriorProjectContent.tsx';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

import { ProjectModalQueryRenderer } from './ProjectModalQueryRenderer.tsx';

import styles from './InteriorProjectWrapper.module.css';

import { type InteriorProjectWrapper_viewer$key } from './__generated__/InteriorProjectWrapper_viewer.graphql.ts';

const getUserId = (): string => getBuyerId(document.cookie);

type ModalState = {
    currentUrl: string | null;
    isOpen: boolean;
};

type Props = {
    viewer: InteriorProjectWrapper_viewer$key;
    isPreview: boolean;
};

const InteriorProjectWrapper: FC<Props> = props => {
    const { isPreview, viewer: viewerRef } = props;

    const viewer = useFragment(
        graphql`
            fragment InteriorProjectWrapper_viewer on Viewer {
                tradeInteriorProjectQuery(projectId: $projectId, loadPreview: $loadPreview) {
                    project {
                        url
                        name
                    }
                    ...InteriorProjectContent_searchResult
                    ...ProjectBreadcrumbs_searchResult
                    ...ProjectModalQueryRenderer_searchResult
                }
                ...InteriorProjectContent_viewer
            }
        `,
        viewerRef
    );

    const searchResult = viewer?.tradeInteriorProjectQuery;

    const [modalState, setModalState] = useState<ModalState>({
        currentUrl: null,
        isOpen: false,
    });

    const [userId, setUserId] = useState('');

    useEffect(() => {
        trackPageView();
    }, []);

    useEffect(() => {
        setUserId(getUserId());
        addLoginHandlers(() => {
            setUserId(getUserId());
        });
    }, []);

    const closeModal = useCallback((): void => {
        const { url: projectUrl, name: projectName } = searchResult?.project || {};

        setModalState({
            currentUrl: null,
            isOpen: false,
        });

        if (window.history.state) {
            window.history.pushState(
                projectUrl,
                projectName || '',
                handleLocaleUrl(projectUrl, GLOBAL_CLIENT_ONLY_LOCALE)
            );
            // reset back to normal handling
            window.onpopstate = null;
        }
    }, [searchResult]);

    const openModal = useCallback((currentUrl: string) => {
        setModalState({
            currentUrl,
            isOpen: true,
        });
    }, []);

    return (
        <div className={styles.wrapper}>
            {isPreview && <div className={styles.disableProjectsOverlay} />}
            <ProjectBreadcrumbs searchResult={searchResult} />
            <InteriorProjectContent
                userId={userId}
                openModal={openModal}
                searchResult={searchResult}
                viewer={viewer}
            />
            {modalState.currentUrl && (
                <ModalContainer
                    isOpen={modalState.isOpen}
                    onClose={closeModal}
                    modalSize="fullscreen"
                >
                    <ProjectModalQueryRenderer
                        photoPath={modalState.currentUrl}
                        searchResult={searchResult}
                        onCloseModal={closeModal}
                        userId={userId}
                    />
                </ModalContainer>
            )}
        </div>
    );
};

export default InteriorProjectWrapper;
